import { useEffect, useState, useRef } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import {
  MagnifyingGlass, DownloadSimple, MapPin, Users as UsersIcon, ShieldCheck,
  Basket, Storefront, Receipt, CurrencyCircleDollar, User, Storefront as Shop, Tag,
  CalendarBlank, SignIn, Clock, GlobeHemisphereWest, LockSimple, LockSimpleOpen,
  PaperPlaneTilt, Info, CheckSquare, Square, PencilSimple, FloppyDisk, X as XIcon,
  Phone, IdentificationCard, Coins, Globe, Megaphone, AndroidLogo, AppleLogo, DeviceMobile, ArrowClockwise, Gift, ArrowUp, ArrowDown,
} from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Card, Loading, catColor } from "@/components/riq";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import SortableTable from "@/components/SortableTable";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import { useAdminNav } from "@/pages/adminNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const AVATAR_COLORS = ["#6E3FFC", "#42C97C", "#F5A623", "#FF6B6B", "#2BB0C4", "#B36EFF", "#FF9F1C", "#E0509E"];
const zar = (v) => money(v, "ZAR");

function initials(name, email) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "?";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2)).toUpperCase();
}
function colorFor(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function Avatar({ name, email, picture, size = 36 }) {
  if (picture) return <img src={picture} alt="" style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  return (
    <div style={{ background: colorFor(email || name), width: size, height: size, fontSize: size * 0.36 }} className="rounded-full flex items-center justify-center text-white font-bold shrink-0">
      {initials(name, email)}
    </div>
  );
}

function DeviceBreakdown({ data }) {
  if (data === false || (data && data.total === 0)) return null;
  const OS_META = {
    Android: { icon: AndroidLogo, color: "#42C97C" },
    Apple: { icon: AppleLogo, color: "#111827" },
    Other: { icon: DeviceMobile, color: "#6E3FFC" },
    Unknown: { icon: DeviceMobile, color: "#CBD1DE" },
  };
  return (
    <Card data-testid="devices-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2">
          <DeviceMobile size={18} weight="duotone" /> Devices & browsers
        </h3>
        {data && (
          <span className="text-xs text-muted-foreground">
            {data.known} of {data.total} users tracked
          </span>
        )}
      </div>

      {!data ? <Loading /> : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* OS split */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Device type</div>
            <div className="space-y-3">
              {data.os.map((o) => {
                const meta = OS_META[o.label] || OS_META.Other;
                const Icon = meta.icon;
                return (
                  <div key={o.label} data-testid={`device-os-${o.label.toLowerCase()}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-1.5 font-medium">
                        <Icon size={16} weight="fill" style={{ color: meta.color }} /> {o.label}
                      </span>
                      <span className="text-muted-foreground text-xs"><b className="text-riq-primary">{o.count}</b> · {o.pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${o.pct}%`, background: meta.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Browser split */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Browser</div>
            {data.browsers.filter((b) => b.label !== "Unknown").length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">No browser data yet.</div>
            ) : (
              <div className="space-y-2.5">
                {data.browsers.map((b, i) => (
                  <div key={b.label} className="flex items-center gap-3" data-testid={`device-browser-${b.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="w-28 shrink-0 text-sm font-medium truncate">{b.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: AVATAR_COLORS[i % AVATAR_COLORS.length] }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-20 text-right shrink-0"><b className="text-riq-primary">{b.count}</b> · {b.pct}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {data && data.os.find((o) => o.label === "Unknown" && o.count > 0) && (
        <div className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>“Unknown” users haven’t signed in since device tracking was added — their device fills in on next login.</span>
        </div>
      )}
    </Card>
  );
}

export default function AdminUsers({ openUserId }) {
  const { openReceipts } = useAdminNav();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(false);
  const [rows, setRows] = useState(null);
  const [cities, setCities] = useState([]);
  const [devices, setDevices] = useState(null);
  const [detail, setDetail] = useState(null); // detail data
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [emailTargets, setEmailTargets] = useState(null); // array of {user_id,email,name} or null

  const load = (q = search, a = active) => {
    setRows(null);
    setSelected(new Set());
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    params.set("sort", "gross");
    if (a) params.set("active", "true");
    api.get(`/admin/users?${params}`).then((r) => setRows(r.data)).catch(() => setRows(false));
  };

  useEffect(() => { load(search, active); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => { api.get("/admin/users/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/users/devices").then((r) => setDevices(r.data)).catch(() => setDevices(false)); }, []);
  useEffect(() => {
    if (openUserId?.id) openUser({ user_id: openUserId.id });
    /* eslint-disable-next-line */
  }, [openUserId]);

  const toggleSel = (uid) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(uid) ? n.delete(uid) : n.add(uid);
    return n;
  });
  const allSelected = rows && rows.length > 0 && rows.every((u) => selected.has(u.user_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set((rows || []).map((u) => u.user_id)));
  const emailSelected = () => {
    const targets = (rows || []).filter((u) => selected.has(u.user_id)).map((u) => ({ user_id: u.user_id, email: u.email, name: u.name }));
    if (targets.length) setEmailTargets(targets);
  };

  const openUser = async (u) => {
    setDetail({ __loading: true, user: u });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}`);
      setDetail(data);
    } catch {
      toast.error("Could not load user detail.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      toast.success(`Role updated to ${role}`);
      setDetail((d) => (d && d.user ? { ...d, user: { ...d.user, role } } : d));
      load(search, active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role.");
    }
  };

  const lockUser = async (userId, locked) => {
    try {
      await api.put(`/admin/users/${userId}/lock`, { locked });
      toast.success(locked ? "User locked out" : "User unlocked");
      setDetail((d) => (d && d.user ? { ...d, user: { ...d.user, locked }, stats: { ...d.stats, locked } } : d));
      load(search, active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update lock state.");
    }
  };

  const updateUser = async (userId, payload) => {
    const { data } = await api.patch(`/admin/users/${userId}`, payload);
    setDetail(data);
    load(search, active);
    return data;
  };

  const adjustMoolas = async (userId, amount, note) => {
    const { data } = await api.post(`/admin/users/${userId}/moolas/adjust`, { amount, note });
    setDetail(data);
    load(search, active);
    return data;
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("riq_token");
    try {
      const res = await fetch(`${API}/admin/users/export${search ? `?search=${encodeURIComponent(search)}` : ""}`, {
        credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sliply_users.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export users CSV."); }
  };

  if (rows === false) return <div className="text-muted-foreground">Could not load users.</div>;

  const totalGross = rows ? rows.reduce((a, r) => a + r.gross, 0) : 0;
  const totalScans = rows ? rows.reduce((a, r) => a + r.receipts, 0) : 0;
  const maxCity = Math.max(1, ...cities.map((c) => c.gross));

  return (
    <div className="space-y-5" data-testid="admin-users">
      {/* Announcement broadcast */}
      <BroadcastCard />

      {/* Top cities */}
      {cities.length > 0 && (
        <Card data-testid="cities-card">
          <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
            <MapPin size={18} weight="duotone" /> Where your users shop
          </h3>
          <div className="space-y-2.5">
            {cities.slice(0, 8).map((c, i) => (
              <div key={i} data-testid={`city-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{c.city}</span>
                  <span className="text-muted-foreground text-xs">{c.users} user{c.users === 1 ? "" : "s"} · {c.receipts} scans · <b className="text-riq-primary">{zar(c.gross)}</b></span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.gross / maxCity) * 100}%`, background: AVATAR_COLORS[i % AVATAR_COLORS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Device & browser split */}
      <DeviceBreakdown data={devices} />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(search.trim(), active); }} className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="riq-input !pl-10 w-full" />
        </form>
        <button data-testid="filter-active" onClick={() => setActive((a) => !a)}
          className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${active ? "bg-riq-secondary text-riq-primary border border-riq-primary" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
          {active ? "✓ " : ""}Active only
        </button>
        <button data-testid="users-export-btn" onClick={downloadCsv} className="flex items-center justify-center gap-1.5 rounded-full border border-riq-border px-4 py-2.5 text-sm font-medium hover:border-riq-primary text-neutral-600">
          <DownloadSimple size={16} weight="bold" /> Export
        </button>
      </div>

      {!rows ? <Loading /> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={UsersIcon} label="Users" value={rows.length} />
            <Stat icon={Receipt} label="Total scans" value={totalScans} />
            <Stat icon={CurrencyCircleDollar} label="Total gross" value={zar(totalGross)} />
          </div>

          {/* Bulk email toolbar */}
          <div className="flex items-center gap-3 min-h-[36px]" data-testid="users-bulk-bar">
            <span className="text-sm text-muted-foreground">{selected.size > 0 ? `${selected.size} selected` : "Select users to email them"}</span>
            {selected.size > 0 && (
              <>
                <button data-testid="email-selected-btn" onClick={emailSelected} className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-1.5 text-sm font-semibold">
                  <PaperPlaneTilt size={14} weight="bold" /> Email selected
                </button>
                <button onClick={() => setSelected(new Set())} className="text-sm text-muted-foreground hover:text-riq-primary underline">Clear</button>
              </>
            )}
          </div>

          <Card className="!p-0 overflow-hidden">
            <SortableTable
              columns={[
                {
                  key: "select", label: "", sortable: false, width: "36px",
                  render: (u) => (
                    <button data-testid={`user-select-${u.user_id}`} onClick={(e) => { e.stopPropagation(); toggleSel(u.user_id); }} className="text-riq-primary">
                      {selected.has(u.user_id) ? <CheckSquare size={18} weight="fill" /> : <Square size={18} className="text-neutral-300" />}
                    </button>
                  ),
                },
                {
                  key: "email", label: "User", type: "text",
                  render: (u) => (
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar name={u.name} email={u.email} picture={u.picture} size={32} />
                      <div className="min-w-0">
                        <button
                          data-testid={`user-receipts-link-${u.user_id}`}
                          onClick={(e) => { e.stopPropagation(); openReceipts({ user_id: u.user_id, user_email: u.email }); }}
                          className="text-sm font-semibold text-riq-primary truncate flex items-center gap-1.5 hover:underline text-left max-w-full"
                          title="Show this user's receipts"
                        >
                          <span className="truncate">{u.name || u.email.split("@")[0]}</span>
                          {u.role && u.role !== "user" && <RoleTag role={u.role} />}
                          {u.locked && <span data-testid={`locked-tag-${u.user_id}`} className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-600">Locked</span>}
                        </button>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  ),
                },
                { key: "receipts", label: "Scans", type: "number", align: "right" },
                { key: "gross", label: "Value", type: "number", align: "right", render: (u) => <span className="font-semibold">{zar(u.gross)}</span> },
                { key: "last_active", label: "Last active", type: "date", render: (u) => <span className="text-muted-foreground whitespace-nowrap">{(u.last_active || "").slice(0, 10) || "—"}</span> },
                { key: "location", label: "Location", type: "text", render: (u) => <span className="text-muted-foreground truncate flex items-center gap-1 max-w-[160px]"><MapPin size={12} className="shrink-0" />{u.location || "—"}</span> },
                { key: "created_at", label: "Joined", type: "date", render: (u) => <span className="text-muted-foreground whitespace-nowrap">{(u.created_at || "").slice(0, 10) || "—"}</span> },
                {
                  key: "actions", label: "", sortable: false, align: "right",
                  render: (u) => (
                    <div className="flex items-center gap-1.5 justify-end">
                      <button data-testid={`user-email-btn-${u.user_id}`} onClick={(e) => { e.stopPropagation(); setEmailTargets([{ user_id: u.user_id, email: u.email, name: u.name }]); }}
                        className="p-1.5 rounded-lg hover:bg-secondary text-neutral-500 hover:text-riq-primary" title="Email user"><PaperPlaneTilt size={16} weight="duotone" /></button>
                      <button data-testid={`user-details-btn-${u.user_id}`} onClick={(e) => { e.stopPropagation(); openUser(u); }}
                        className="p-1.5 rounded-lg hover:bg-secondary text-neutral-500 hover:text-riq-primary" title="Details"><Info size={16} weight="duotone" /></button>
                    </div>
                  ),
                },
              ]}
              rows={rows}
              defaultSort={[{ key: "last_active", dir: "desc" }, { key: "receipts", dir: "desc" }]}
              rowKey="user_id"
              onRowClick={openUser}
              testIdPrefix="user-row"
              empty="No users found."
            />
          </Card>
        </>
      )}

      <UserDetailDialog detail={detail} loading={detailLoading} onClose={() => setDetail(null)} onChangeRole={changeRole} onLock={lockUser} onUpdate={updateUser} onAdjustMoolas={adjustMoolas}
        onEmail={(u) => setEmailTargets([{ user_id: u.user_id, email: u.email, name: u.name }])}
        onViewReceipts={(u) => { setDetail(null); openReceipts({ user_id: u.user_id, user_email: u.email }); }} />
      <EmailComposeDialog open={!!emailTargets} recipients={emailTargets || []} onClose={() => setEmailTargets(null)} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <Icon size={18} weight="duotone" className="text-riq-primary mb-2" />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-0.5 truncate">{value}</div>
    </Card>
  );
}

function BroadcastCard() {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  return (
    <Card data-testid="broadcast-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-riq-primary/10 flex items-center justify-center shrink-0">
            <Megaphone size={18} weight="duotone" className="text-riq-primary" />
          </div>
          <div>
            <div className="font-head font-semibold text-riq-primary">Announcements</div>
            <div className="text-xs text-muted-foreground">Email every user — the “forward your receipts” feature, or your own message.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="open-custom-broadcast-btn" onClick={() => setCustomOpen(true)}
            className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm font-medium hover:border-riq-primary text-neutral-600">
            <PencilSimple size={14} weight="bold" /> New announcement
          </button>
          <button data-testid="open-broadcast-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
            <Megaphone size={14} weight="bold" /> Announce email-in
          </button>
        </div>
      </div>
      <BroadcastDialog open={open} onClose={() => setOpen(false)} />
      <CustomBroadcastDialog open={customOpen} onClose={() => setCustomOpen(false)} />
    </Card>
  );
}

function useBroadcastProgress() {
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);
  const start = (broadcastId, total) => {
    setStatus({ status: "running", sent: 0, failed: 0, total });
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/admin/broadcast/${broadcastId}`);
        setStatus(r.data);
        if (r.data.status === "completed") {
          clearInterval(pollRef.current);
          toast.success(`Broadcast complete — ${r.data.sent} sent${r.data.failed ? `, ${r.data.failed} failed` : ""}`);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };
  const reset = () => { clearInterval(pollRef.current); setStatus(null); };
  useEffect(() => () => clearInterval(pollRef.current), []);
  return { status, start, reset };
}

function CustomBroadcastDialog({ open, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { status, start, reset } = useBroadcastProgress();

  useEffect(() => {
    if (!open) { reset(); setConfirming(false); setSending(false); }
  }, [open]);

  const valid = subject.trim() && body.trim();

  const sendTest = async () => {
    if (!valid) { toast.error("Add a subject and message first"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/broadcast/custom", { test: true, subject, body });
      if (data.ok) toast.success(`Test sent to ${data.to}`);
      else toast.error(data.error || "Test failed to send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test"); }
    finally { setTesting(false); }
  };

  const sendAll = async () => {
    setSending(true); setConfirming(false);
    try {
      const { data } = await api.post("/admin/broadcast/custom", { test: false, subject, body });
      start(data.broadcast_id, data.total);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start broadcast"); setSending(false); }
  };

  const pct = status && status.total ? Math.round(((status.sent + status.failed) / status.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="custom-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PencilSimple size={20} weight="duotone" className="text-riq-primary" /> New announcement</DialogTitle>
          <DialogDescription>Send a custom message to every user, from <b>info@sliply.co.za</b>. Personalised with each user's first name and wrapped in the Sliply template.</DialogDescription>
        </DialogHeader>

        {status ? (
          <div className="space-y-2" data-testid="custom-broadcast-progress">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{status.status === "completed" ? "Completed" : "Sending…"}</span><span className="font-semibold text-riq-primary">{status.sent + status.failed} / {status.total}</span></div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-riq-primary transition-all" style={{ width: `${pct}%` }} /></div>
            <div className="text-xs text-muted-foreground">{status.sent} sent{status.failed ? ` · ${status.failed} failed` : ""}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Subject</span>
              <input data-testid="custom-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120}
                placeholder="e.g. New: price alerts are here" className="riq-input w-full mt-1" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Message</span>
              <textarea data-testid="custom-body" value={body} onChange={(e) => setBody(e.target.value)} rows={7}
                placeholder={"Write your message…\n\nUse a blank line to start a new paragraph."} className="riq-input w-full mt-1 resize-y leading-relaxed" />
              <span className="text-[11px] text-muted-foreground">Each user is greeted by first name automatically; an “Open Sliply →” button is added at the end.</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
              <button data-testid="custom-test-btn" onClick={sendTest} disabled={testing || !valid}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-riq-border px-4 py-2.5 text-sm font-medium hover:border-riq-primary disabled:opacity-50">
                <PaperPlaneTilt size={15} weight="bold" /> {testing ? "Sending…" : "Send test to me"}
              </button>
              {!confirming ? (
                <button data-testid="custom-sendall-btn" onClick={() => setConfirming(true)} disabled={!valid}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
                  <Megaphone size={15} weight="bold" /> Send to all users
                </button>
              ) : (
                <button data-testid="custom-confirm-btn" onClick={sendAll} disabled={sending}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-[#b3261e] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
                  {sending ? "Starting…" : "Confirm — send to everyone"}
                </button>
              )}
            </div>
            {confirming && <p className="text-xs text-[#b3261e] text-center">This emails every registered user. This can't be undone.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ open, onClose }) {
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { status, start, reset } = useBroadcastProgress();

  useEffect(() => {
    if (!open) { reset(); setConfirming(false); setSending(false); }
  }, [open]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/broadcast/feature-email", { test: true });
      if (data.ok) toast.success(`Test sent to ${data.to}`);
      else toast.error(data.error || "Test failed to send");
    } catch { toast.error("Could not send test"); }
    finally { setTesting(false); }
  };

  const sendAll = async () => {
    setSending(true); setConfirming(false);
    try {
      const { data } = await api.post("/admin/broadcast/feature-email", { test: false });
      start(data.broadcast_id, data.total);
    } catch { toast.error("Could not start broadcast"); setSending(false); }
  };

  const pct = status && status.total ? Math.round(((status.sent + status.failed) / status.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone size={20} weight="duotone" className="text-riq-primary" /> Announce “email your receipts”</DialogTitle>
          <DialogDescription>Sends the branded announcement to every user, from <b>info@sliply.co.za</b>. Replies go to the same inbox.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-riq-border bg-secondary/40 p-4 text-sm text-neutral-600 leading-relaxed">
          <div className="font-semibold text-riq-primary mb-1">📸 New: just forward your receipts to us — we'll do the rest</div>
          Users learn they can forward any receipt to <b>scan@sliply.co.za</b> (from their account email) and it's added automatically. Sent as personalised “Hi {"{first name}"}”.
        </div>

        {status ? (
          <div className="space-y-2" data-testid="broadcast-progress">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{status.status === "completed" ? "Completed" : "Sending…"}</span><span className="font-semibold text-riq-primary">{status.sent + status.failed} / {status.total}</span></div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-riq-primary transition-all" style={{ width: `${pct}%` }} /></div>
            <div className="text-xs text-muted-foreground">{status.sent} sent{status.failed ? ` · ${status.failed} failed` : ""}</div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <button data-testid="broadcast-test-btn" onClick={sendTest} disabled={testing}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-riq-border px-4 py-2.5 text-sm font-medium hover:border-riq-primary disabled:opacity-60">
              <PaperPlaneTilt size={15} weight="bold" /> {testing ? "Sending…" : "Send test to me"}
            </button>
            {!confirming ? (
              <button data-testid="broadcast-sendall-btn" onClick={() => setConfirming(true)}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2.5 text-sm font-semibold">
                <Megaphone size={15} weight="bold" /> Send to all users
              </button>
            ) : (
              <button data-testid="broadcast-confirm-btn" onClick={sendAll} disabled={sending}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-[#b3261e] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
                {sending ? "Starting…" : "Confirm — send to everyone"}
              </button>
            )}
          </div>
        )}
        {confirming && !status && <p className="text-xs text-[#b3261e] text-center">This emails every registered user. This can't be undone.</p>}
      </DialogContent>
    </Dialog>
  );
}


function UserDetailDialog({ detail, loading, onClose, onChangeRole, onLock, onUpdate, onAdjustMoolas, onEmail, onViewReceipts }) {
  const open = !!detail;
  const u = detail?.user;
  const s = detail?.stats;
  const mom = s ? s.scans_this_month - s.scans_last_month : 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar name={u?.name} email={u?.email} picture={u?.picture} size={40} />
            <div className="min-w-0 text-left">
              <div className="text-base font-bold text-riq-primary truncate flex items-center gap-2">
                {u?.name || u?.email?.split("@")[0]}
                {s?.locked && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">Locked</span>}
              </div>
              <div className="text-xs font-normal text-muted-foreground truncate">{u?.email}</div>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">User details, stats, role and access control</DialogDescription>
        </DialogHeader>

        {loading || detail?.__loading ? (
          <div className="py-10"><Loading /></div>
        ) : detail && s ? (
          <div className="space-y-5">
            {/* All-time */}
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="All-time scans" value={s.receipts} />
              <MiniStat label="All-time value" value={zar(s.gross)} />
              <MiniStat label="Avg / receipt" value={zar(s.avg)} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button data-testid="detail-view-receipts" onClick={() => onViewReceipts?.(u)}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-riq-primary text-riq-primary py-2.5 text-sm font-semibold hover:bg-riq-secondary">
                <Receipt size={16} weight="bold" /> View receipts
              </button>
              <button data-testid="detail-email-user" onClick={() => onEmail?.(u)}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-riq-primary text-white py-2.5 text-sm font-semibold">
                <PaperPlaneTilt size={16} weight="bold" /> Email user
              </button>
            </div>

            {/* Editable profile & account info */}
            <ProfileEditor user={u} onUpdate={onUpdate} />

            {/* Moola adjustment (add / reverse with a note) */}
            <MoolaAdjust user={u} adjustments={detail?.moola_adjustments} onAdjust={onAdjustMoolas} />

            {/* Bulk re-run AI totals for this user's receipts */}
            <BulkRerun user={u} />

            {/* This vs last month */}
            <div className="rounded-2xl border border-riq-border p-3" data-testid="user-month-compare">
              <div className="text-xs font-semibold text-muted-foreground mb-2.5">This month vs last month</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">This month</div>
                  <div className="font-head text-lg font-bold text-riq-primary">{s.scans_this_month} <span className="text-xs font-normal text-muted-foreground">scans</span></div>
                  <div className="text-xs text-muted-foreground">{zar(s.gross_this_month)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last month</div>
                  <div className="font-head text-lg font-bold text-neutral-500">{s.scans_last_month} <span className="text-xs font-normal text-muted-foreground">scans</span></div>
                  <div className="text-xs text-muted-foreground">{zar(s.gross_last_month)}</div>
                </div>
              </div>
              {(s.scans_this_month > 0 || s.scans_last_month > 0) && (
                <div className={`mt-2 text-xs font-medium ${mom >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {mom >= 0 ? "▲" : "▼"} {Math.abs(mom)} scans vs last month
                </div>
              )}
            </div>

            {/* Account / login facts */}
            <div className="grid grid-cols-2 gap-2 text-xs" data-testid="user-account-facts">
              <Fact icon={CalendarBlank} label="Signed up" value={u?.created_at ? String(u.created_at).slice(0, 10) : "—"} />
              <Fact icon={SignIn} label="Logins this month" value={s.logins_this_month} />
              <Fact icon={SignIn} label="Total logins" value={s.logins_total} />
              <Fact icon={Clock} label="Last active" value={s.last ? String(s.last).slice(0, 10) : "—"} />
              <Fact icon={Clock} label="Last login" value={s.last_login_at ? String(s.last_login_at).slice(0, 10) : "—"} />
              <Fact icon={GlobeHemisphereWest} label="Login area" value={s.last_login_area || "—"} />
              <Fact icon={MapPin} label="Receipt area" value={s.receipt_area || "—"} />
              <Fact icon={MapPin} label="Login IP" value={s.last_login_ip || "—"} />
              <Fact
                icon={s.last_device_os === "Android" ? AndroidLogo : s.last_device_os === "Apple" ? AppleLogo : DeviceMobile}
                label="Device" value={s.last_device_os || "—"}
              />
              <Fact icon={Globe} label="Browser" value={s.last_browser || "—"} />
            </div>

            <RoleAssign role={u?.role || "user"} onChange={(r) => onChangeRole(u.user_id, r)} />

            {/* Lock control */}
            <div className="rounded-2xl border border-riq-border p-3" data-testid="user-lock-control">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-riq-primary flex items-center gap-1.5">
                    {s.locked ? <LockSimple size={15} weight="fill" /> : <LockSimpleOpen size={15} weight="duotone" />}
                    {s.locked ? "Account locked" : "Account active"}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                    {s.locked ? "This user can't log in and their sessions were ended." : "Locking blocks login and ends active sessions."}
                  </div>
                </div>
                <button
                  data-testid="user-lock-toggle"
                  onClick={() => onLock(u.user_id, !s.locked)}
                  className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${s.locked ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}`}
                >
                  {s.locked ? "Unlock" : "Lock access"}
                </button>
              </div>
            </div>

            {detail.monthly.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Spend timeline</div>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={detail.monthly} margin={{ left: -18, right: 4, top: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ud-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6E3FFC" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#6E3FFC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(v) => zar(v)} />
                    <Area type="monotone" dataKey="gross" stroke="#6E3FFC" strokeWidth={2} fill="url(#ud-grad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <DetailList title="Top merchants" icon={Storefront} rows={detail.top_merchants} />
            <DetailList title="Top categories" icon={Basket} rows={detail.top_categories} colorByLabel />

            {detail.recent.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Recent receipts</div>
                <div className="divide-y divide-riq-border">
                  {detail.recent.map((r, i) => (
                    <div key={i} className="flex items-center py-2 text-sm">
                      <span className="flex-1 min-w-0 truncate font-medium">{r.merchant_name}</span>
                      <span className="text-xs text-muted-foreground mx-2">{(r.date || "").slice(0, 10)}</span>
                      <span className="font-semibold w-24 text-right">{money(r.total, r.currency || "ZAR")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.statement && <AdminMoolaStatement statement={detail.statement} />}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const ADMIN_REDEMPTION_STATUS = {
  pending: { label: "Pending", cls: "bg-riq-gold/25 text-[#8a5a00]" },
  fulfilled: { label: "Fulfilled", cls: "bg-[#E4F7ED] text-[#22B573]" },
  rejected: { label: "Rejected", cls: "bg-[#FFE3E1] text-[#b3261e]" },
};

function AdminMoolaStatement({ statement }) {
  const [showAll, setShowAll] = useState(false);
  const t = statement.totals || {};
  const txns = statement.transactions || [];
  const redemptions = statement.redemptions || [];
  const shown = showAll ? txns : txns.slice(0, 5);
  const TXN = {
    earn: { Icon: ArrowUp, cls: "bg-[#E4F7ED] text-[#22B573]" },
    redeem: { Icon: Gift, cls: "bg-riq-primary/10 text-riq-primary" },
    reversal: { Icon: ArrowDown, cls: "bg-[#FFE3E1] text-[#b3261e]" },
  };
  const state = (r) => r.status === "rejected" ? ADMIN_REDEMPTION_STATUS.rejected
    : (r.fulfilment_status || "").toLowerCase() === "fulfilled" ? ADMIN_REDEMPTION_STATUS.fulfilled
    : ADMIN_REDEMPTION_STATUS.pending;

  return (
    <div data-testid="admin-moola-statement" className="rounded-xl border border-riq-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/60">
        <Coins size={16} weight="duotone" className="text-riq-gold" />
        <span className="text-sm font-semibold text-riq-primary">Moolas statement</span>
        <span className="ml-auto text-sm font-head font-bold text-riq-primary">{statement.balance} <span className="text-riq-gold text-xs">Moolas</span></span>
      </div>
      <div className="grid grid-cols-3 divide-x divide-riq-border border-b border-riq-border text-center">
        <div className="py-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Earned</div><div className="font-bold text-[#22B573]">+{t.earned || 0}</div></div>
        <div className="py-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Redeemed</div><div className="font-bold text-riq-primary">-{t.redeemed || 0}</div></div>
        <div className="py-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Reversed</div><div className="font-bold text-[#b3261e]">-{t.reversed || 0}</div></div>
      </div>

      {redemptions.length > 0 && (
        <div className="px-4 pt-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Redemptions</div>
          <div className="space-y-1.5">
            {redemptions.map((r) => {
              const st = state(r);
              return (
                <div key={r.id} data-testid={`admin-statement-redemption-${r.id}`} className="flex items-center gap-2 text-sm">
                  <Gift size={15} weight="duotone" className="text-riq-primary shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{r.title}</span>
                  <span className="text-xs text-muted-foreground">{shortDate(r.at)} · {r.cost}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Activity</div>
        {txns.length === 0 ? (
          <div className="text-sm text-muted-foreground">No Moolas activity.</div>
        ) : (
          <div className="divide-y divide-riq-border">
            {shown.map((x, i) => {
              const meta = TXN[x.type] || { Icon: ArrowClockwise, cls: "bg-secondary text-neutral-600" };
              const pos = (x.amount || 0) > 0;
              return (
                <div key={i} className="flex items-center gap-2 py-2 text-sm">
                  <span className={`grid h-7 w-7 place-items-center rounded-md shrink-0 ${meta.cls}`}><meta.Icon size={13} weight="bold" /></span>
                  <span className="flex-1 min-w-0 truncate">{x.reason || (x.type === "earn" ? "Earned Moolas" : "Adjustment")}</span>
                  <span className="text-xs text-muted-foreground mx-1">{shortDate(x.at)}</span>
                  <span className={`font-bold tabular-nums w-12 text-right ${pos ? "text-[#22B573]" : "text-[#b3261e]"}`}>{pos ? "+" : ""}{x.amount}</span>
                </div>
              );
            })}
            {txns.length > 5 && (
              <button onClick={() => setShowAll((s) => !s)} className="w-full pt-2 text-sm text-riq-primary font-medium">
                {showAll ? "Show less" : `Show all ${txns.length}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileEditor({ user, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    setEditing(false);
    setForm({
      name: user?.name || "",
      currency: (user?.currency || "ZAR").toUpperCase(),
      country: user?.country || "",
      phone: user?.phone || "",
      id_number: user?.id_number || "",
      moolas: user?.moolas ?? 0,
    });
  }, [user?.user_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(user.user_id, {
        name: form.name,
        currency: form.currency,
        country: form.country,
        phone: form.phone,
        id_number: form.id_number,
        moolas: Number(form.moolas) || 0,
      });
      toast.success("User profile updated");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="user-profile-editor" className="rounded-2xl border border-riq-border p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-muted-foreground">Profile &amp; account</div>
        {!editing ? (
          <button data-testid="profile-edit-btn" onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1 text-xs font-semibold text-riq-primary hover:border-riq-primary">
            <PencilSimple size={13} weight="bold" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button data-testid="profile-cancel-btn" onClick={() => setEditing(false)}
              className="flex items-center gap-1 rounded-full border border-riq-border px-3 py-1 text-xs font-semibold text-neutral-500 hover:border-neutral-400">
              <XIcon size={13} weight="bold" /> Cancel
            </button>
            <button data-testid="profile-save-btn" onClick={save} disabled={saving}
              className="flex items-center gap-1 rounded-full bg-riq-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
              <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Fact icon={User} label="Name" value={user?.name || "—"} />
          <Fact icon={CurrencyCircleDollar} label="Base currency" value={(user?.currency || "ZAR").toUpperCase()} />
          <Fact icon={Globe} label="Country" value={user?.country || "—"} />
          <Fact icon={Phone} label="Phone" value={user?.phone || "—"} />
          <Fact icon={IdentificationCard} label="ID number" value={user?.id_number || "—"} />
          <Fact icon={Coins} label="Moolas balance" value={user?.moolas ?? 0} />
          <Fact icon={ShieldCheck} label="Auth provider" value={user?.auth_provider || "password"} />
          <Fact icon={CalendarBlank} label="User ID" value={user?.user_id || "—"} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <EditField label="Name">
            <input data-testid="edit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="riq-input w-full" placeholder="Full name" />
          </EditField>
          <EditField label="Base currency">
            <CurrencyCombobox testId="edit-currency" value={form.currency} onChange={(c) => set("currency", c)} />
          </EditField>
          <EditField label="Country">
            <input data-testid="edit-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="riq-input w-full" placeholder="e.g. South Africa" />
          </EditField>
          <EditField label="Phone">
            <input data-testid="edit-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="riq-input w-full" placeholder="e.g. 0821234567" />
          </EditField>
          <EditField label="ID number">
            <input data-testid="edit-id-number" value={form.id_number} onChange={(e) => set("id_number", e.target.value)} className="riq-input w-full" placeholder="13-digit SA ID" />
          </EditField>
          <EditField label="Moolas balance">
            <input data-testid="edit-moolas" type="number" min="0" value={form.moolas} onChange={(e) => set("moolas", e.target.value)} className="riq-input w-full" />
          </EditField>
        </div>
      )}
    </div>
  );
}

function MoolaAdjust({ user, adjustments, onAdjust }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOpen(false); setAmount(""); setNote(""); }, [user?.user_id]);

  const apply = async (sign) => {
    const n = Math.abs(parseInt(amount, 10) || 0);
    if (!n) { toast.error("Enter an amount"); return; }
    if (!note.trim()) { toast.error("A note is required"); return; }
    setBusy(true);
    try {
      await onAdjust(user.user_id, sign * n, note.trim());
      toast.success(sign > 0 ? `Added ${n} Moolas` : `Reversed ${n} Moolas`);
      setAmount(""); setNote(""); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adjust Moolas.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="moola-adjust" className="rounded-2xl border border-riq-border p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Coins size={14} weight="fill" className="text-riq-gold" /> Moola balance: <b className="text-riq-primary">{user?.moolas ?? 0}</b></div>
        {!open && (
          <button data-testid="moola-adjust-open" onClick={() => setOpen(true)}
            className="rounded-full border border-riq-border px-3 py-1 text-xs font-semibold text-riq-primary hover:border-riq-primary">
            Adjust
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <input data-testid="moola-adjust-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount" className="riq-input w-28" />
            <input data-testid="moola-adjust-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Reason / note (required)" className="riq-input flex-1" />
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="moola-add-btn" onClick={() => apply(1)} disabled={busy}
              className="flex-1 rounded-full bg-[#22B573] text-white py-2 text-xs font-semibold disabled:opacity-50">+ Add</button>
            <button data-testid="moola-reverse-btn" onClick={() => apply(-1)} disabled={busy}
              className="flex-1 rounded-full bg-red-500 text-white py-2 text-xs font-semibold disabled:opacity-50">− Reverse</button>
            <button data-testid="moola-adjust-cancel" onClick={() => { setOpen(false); setAmount(""); setNote(""); }}
              className="rounded-full border border-riq-border px-3 py-2 text-xs font-semibold text-neutral-500">Cancel</button>
          </div>
          <p className="text-[11px] text-muted-foreground">A note is required and kept as an audit trail. Balance can't go below 0.</p>
        </div>
      )}

      {adjustments?.length > 0 && (
        <div className="mt-3 border-t border-riq-border pt-2 space-y-1.5" data-testid="moola-adjust-history">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recent adjustments</div>
          {adjustments.slice(0, 5).map((a, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="truncate text-neutral-600 flex-1 min-w-0">{a.reason}</span>
              <span className={`font-semibold shrink-0 ml-2 ${a.amount >= 0 ? "text-[#22B573]" : "text-red-500"}`}>{a.amount >= 0 ? "+" : ""}{a.amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BulkRerun({ user }) {
  const [onlyForeign, setOnlyForeign] = useState(true);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => { setJob(null); setRunning(false); setOnlyForeign(true); }, [user?.user_id]);

  const start = async () => {
    setRunning(true); setJob(null);
    try {
      const { data } = await api.post("/admin/receipts/bulk-rerun", { user_id: user.user_id, only_foreign: onlyForeign });
      if (!data.total) { toast.info("No matching receipts to re-run."); setRunning(false); return; }
      setJob({ id: data.job_id, total: data.total, done: 0, fixed: 0, failed: 0, status: "running" });
      poll(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start re-run.");
      setRunning(false);
    }
  };

  const poll = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/receipts/bulk-rerun/${jobId}`);
      setJob(data);
      if (data.status === "done") {
        setRunning(false);
        toast.success(`Re-run complete — ${data.fixed} corrected, ${data.done} scanned${data.failed ? `, ${data.failed} failed` : ""}`);
      } else {
        setTimeout(() => poll(jobId), 2500);
      }
    } catch {
      setRunning(false);
      toast.error("Lost track of the re-run job.");
    }
  };

  const pct = job && job.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div data-testid="bulk-rerun" className="rounded-2xl border border-riq-border p-3">
      <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2">
        <ArrowClockwise size={14} weight="bold" className="text-riq-primary" /> Re-run AI totals & dates
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-600 mb-2.5 cursor-pointer">
        <input data-testid="bulk-rerun-foreign" type="checkbox" checked={onlyForeign} onChange={(e) => setOnlyForeign(e.target.checked)} disabled={running} className="accent-riq-primary" />
        Only foreign-currency receipts (recommended)
      </label>
      <button data-testid="bulk-rerun-start" onClick={start} disabled={running}
        className="w-full rounded-full bg-riq-primary text-white py-2 text-xs font-semibold disabled:opacity-50">
        {running ? "Re-running…" : "Re-run this user's receipts"}
      </button>
      {job && (
        <div className="mt-2.5" data-testid="bulk-rerun-progress">
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-riq-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {job.done}/{job.total} scanned · <b className="text-[#22B573]">{job.fixed} corrected</b>{job.failed ? ` · ${job.failed} failed` : ""}{job.status === "done" ? " · done" : "…"}
          </div>
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Re-reads each stored slip with the latest AI and fixes currency/FX mistakes. May take a while for many receipts.</p>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

const ROLE_DEFS = [
  { k: "admin", l: "Admin", desc: "Administrator functions", icon: ShieldCheck },
  { k: "user", l: "User", desc: "Default app experience", icon: User },
  { k: "merchant", l: "Merchant", desc: "Store owner — see Merchants tab", icon: Shop },
  { k: "brand", l: "Brand", desc: "Brand / vendor — see Merchants tab", icon: Tag },
];

function RoleAssign({ role, onChange }) {
  return (
    <div data-testid="role-assign" className="rounded-2xl border border-riq-border p-3">
      <div className="text-xs font-semibold text-muted-foreground mb-2.5">Assign role</div>
      <div className="grid grid-cols-2 gap-2">
        {ROLE_DEFS.map((r) => {
          const on = role === r.k;
          return (
            <button
              key={r.k}
              data-testid={`role-option-${r.k}`}
              onClick={() => !on && onChange(r.k)}
              className={`text-left rounded-xl border p-2.5 transition-colors ${on ? "border-riq-primary bg-riq-secondary" : "border-riq-border hover:border-riq-primary/50"}`}
            >
              <div className="flex items-center gap-1.5">
                <r.icon size={15} weight={on ? "fill" : "duotone"} className="text-riq-primary shrink-0" />
                <span className="text-sm font-semibold text-riq-primary">{r.l}</span>
                {on && <span className="ml-auto text-[10px] font-bold text-riq-primary">CURRENT</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{r.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoleTag({ role }) {
  const map = {
    admin: { l: "Admin", c: "#6E3FFC", b: "#EDE7FF" },
    merchant: { l: "Merchant", c: "#1f7a48", b: "#E4F7ED" },
    brand: { l: "Brand", c: "#8a5a00", b: "#FFF1D6" },
  };
  const t = map[role];
  if (!t) return null;
  return <span data-testid={`role-tag-${role}`} style={{ color: t.c, background: t.b }} className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">{t.l}</span>;
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-secondary/50 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-lg font-bold text-riq-primary mt-0.5 truncate">{value}</div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-secondary/40 px-3 py-2">
      <Icon size={15} weight="duotone" className="text-riq-primary shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">{label}</div>
        <div className="text-sm font-semibold text-riq-primary truncate mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function DetailList({ title, icon: Icon, rows, colorByLabel }) {
  if (!rows?.length) return null;
  const max = Math.max(1, ...rows.map((r) => r.gross));
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Icon size={14} weight="duotone" /> {title}</div>
      <div className="space-y-2">
        {rows.slice(0, 5).map((r, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-sm">
              <span className="truncate flex-1 font-medium">{r.label}</span>
              <span className="text-muted-foreground text-xs mx-2">{r.count}</span>
              <span className="font-semibold w-20 text-right">{zar(r.gross)}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(r.gross / max) * 100}%`, background: colorByLabel ? catColor(r.label) : "#6E3FFC" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
